import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  computed,
  signal,
  type ElementRef,
  viewChild,
} from '@angular/core';
import {
  NgbCollapseModule,
  NgbDropdown,
  NgbDropdownMenu,
  NgbDropdownToggle,
} from '@ng-bootstrap/ng-bootstrap';
import { RouterLink } from '@angular/router';
import { NgIcon } from '@ng-icons/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { LanguageService } from '@core/services/language.service';
import { TranslocoService } from '@jsverse/transloco';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '@core/services/auth.service';
import { CurrentUserService } from '@core/services/current-user.service';
import { ClickOutsideDirective } from '@core/directive/click-outside.directive';
import { translatePrivilegeLabels } from '@shared/utils/privilege-label.util';

interface SubmenuPosition {
  top: string;
  left: string;
}

@Component({
  selector: 'app-user-profile',
  imports: [
    NgbCollapseModule,
    NgbDropdown,
    NgbDropdownMenu,
    NgbDropdownToggle,
    NgIcon,
    RouterLink,
    TranslocoPipe,
    ClickOutsideDirective,
  ],
  templateUrl: './user-profile.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class UserProfileComponent {
  private readonly languageService = inject(LanguageService);
  private readonly translocoService = inject(TranslocoService);
  private readonly authService = inject(AuthService);
  private readonly currentUserService = inject(CurrentUserService);

  readonly languageDropdownOpen = signal(false);
  readonly submenuPosition = signal<SubmenuPosition>({ top: '0px', left: '0px' });
  readonly languageButtonRef = viewChild<ElementRef<HTMLDivElement>>('languageButton');

  readonly userName = this.currentUserService.userName;

  readonly userRoleLabel = computed(() =>
    translatePrivilegeLabels(
      this.translocoService,
      this.currentUserService.userPrivileges(),
      this.activeLang(),
      'user_profile.no_privileges',
    ),
  );

  readonly userInitials = computed(() => {
    const name = this.userName();
    if (name.trim() === '') return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0]?.charAt(0) ?? ''}${parts[parts.length - 1]?.charAt(0) ?? ''}`.toUpperCase();
    }
    return (parts[0]?.slice(0, 2) ?? '?').toUpperCase();
  });

  readonly availableLanguages = this.languageService.getLanguages();
  readonly currentLang = this.languageService.currentLang;
  readonly showSecurityMenu = true;

  private readonly activeLang = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  readonly languageLabel = computed(() =>
    this.translocoService.translate('user_profile.language', {}, this.activeLang()),
  );

  readonly logoutLabel = computed(() =>
    this.translocoService.translate('user_profile.logout', {}, this.activeLang()),
  );

  toggleLanguageDropdown(): void {
    const isOpen = !this.languageDropdownOpen();
    this.languageDropdownOpen.set(isOpen);
    if (isOpen) this.calculateSubmenuPosition();
  }

  closeLanguageDropdown(): void {
    this.languageDropdownOpen.set(false);
  }

  changeLanguage(code: string): void {
    this.languageService.setLanguage(code);
    this.closeLanguageDropdown();
  }

  private calculateSubmenuPosition(): void {
    const buttonEl = this.languageButtonRef();
    if (buttonEl == null) return;
    const rect = buttonEl.nativeElement.getBoundingClientRect();
    this.submenuPosition.set({ top: `${rect.top}px`, left: `${rect.right + 8}px` });
  }

  logout(): void {
    this.authService.logout();
  }
}
